import { ASSISTANT, ECE, TEACHER } from '@BUP/constants'

const tawkPropertyId = import.meta.env.VITE_TAWK_PROPERTY_ID || '6126489cd6e7610a49b1f310'
const tawkWidgetId = import.meta.env.VITE_TAWK_WIDGET_ID || '1fduon8n5'
const tawkScriptUrl = import.meta.env.VITE_TAWK_SCRIPT_URL || `https://embed.tawk.to/${tawkPropertyId}/${tawkWidgetId}`

let lastIdentityFingerprint = null
let pendingIdentityFingerprint = null
let pendingLoginPayload = null
let hasIdentifiedSession = false
let isOnLoadLoginHookAttached = false

const supportedTawkRoles = [ASSISTANT, TEACHER, ECE]

const getIdentityFingerprint = ({ user, organization, role }) => [
  user?.id,
  user?.name,
  user?.email,
  user?.tawkHash,
  organization?.name,
  organization?.network,
  role?.type
].join('|')

const getTawkLoginPayload = ({ user, organization, role }) => ({
  hash: user.tawkHash,
  userId: String(user.id),
  name: user.name,
  email: user.email,
  'organization-name': organization.name,
  'organization-network': organization.network,
  'user-role': role.type
})

const hasCompleteTawkIdentity = ({ user, organization, role }) => {
  return Boolean(
    user?.id &&
    user?.name &&
    user?.email &&
    organization?.name &&
    organization?.network &&
    role?.type
  )
}

const performTawkLogin = ({ tawkApi, payload, fingerprint }) => {
  if (!tawkApi?.login) return false

  tawkApi.login(payload)
  hasIdentifiedSession = true
  lastIdentityFingerprint = fingerprint
  pendingIdentityFingerprint = null
  pendingLoginPayload = null

  return true
}

const tryLoginPendingIdentity = ({ windowRef }) => {
  if (!pendingLoginPayload || !pendingIdentityFingerprint) return

  performTawkLogin({
    tawkApi: windowRef?.Tawk_API,
    payload: pendingLoginPayload,
    fingerprint: pendingIdentityFingerprint
  })
}

export const isTawkWidgetEnabled = ({ role }) => {
  const roleType = role?.type

  return supportedTawkRoles.includes(roleType)
}

export const getTawkScriptConfig = () => ({
  scriptId: 'bup-tawk-widget-script',
  scriptSrc: tawkScriptUrl,
  propertyId: tawkPropertyId,
  widgetId: tawkWidgetId
})

export const initializeTawkScript = ({
  isEnabled,
  tawkScriptConfig = getTawkScriptConfig(),
  documentRef = document,
  windowRef = window
}) => {
  if (!isEnabled || !tawkScriptConfig?.scriptSrc) {
    windowRef?.Tawk_API?.hideWidget?.()
    return null
  }

  const existingScript = documentRef.getElementById(tawkScriptConfig.scriptId)

  if (existingScript) {
    windowRef?.Tawk_API?.showWidget?.()
    return existingScript
  }

  const script = documentRef.createElement('script')
  script.id = tawkScriptConfig.scriptId
  script.async = true
  script.src = tawkScriptConfig.scriptSrc
  script.charset = 'UTF-8'
  script.setAttribute('crossorigin', '*')

  const firstScript = documentRef.getElementsByTagName('script')[0]
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  }

  return script
}

export const logoutTawkUser = ({ windowRef = window } = {}) => {
  windowRef?.Tawk_API?.logout?.()

  hasIdentifiedSession = false
  lastIdentityFingerprint = null
  pendingIdentityFingerprint = null
  pendingLoginPayload = null
}

export const loginTawkUser = ({ user, organization, role, windowRef = window }) => {
  if (!hasCompleteTawkIdentity({ user, organization, role })) return

  if (!user?.tawkHash) {
    if (hasIdentifiedSession || lastIdentityFingerprint || pendingIdentityFingerprint) {
      logoutTawkUser({ windowRef })
    }
    return
  }

  const fingerprint = getIdentityFingerprint({ user, organization, role })

  if (fingerprint === lastIdentityFingerprint || fingerprint === pendingIdentityFingerprint) {
    return
  }

  const payload = getTawkLoginPayload({ user, organization, role })
  const tawkApi = windowRef.Tawk_API || {}

  windowRef.Tawk_API = tawkApi

  const didLoginImmediately = performTawkLogin({
    tawkApi,
    payload,
    fingerprint
  })

  if (didLoginImmediately) return

  pendingIdentityFingerprint = fingerprint
  pendingLoginPayload = payload

  if (isOnLoadLoginHookAttached) return

  const previousOnLoad = tawkApi.onLoad

  tawkApi.onLoad = () => {
    isOnLoadLoginHookAttached = false

    try {
      previousOnLoad?.()
    } finally {
      tryLoginPendingIdentity({ windowRef })
    }
  }

  isOnLoadLoginHookAttached = true
}

export default getTawkScriptConfig()
